// ============================================================
// Acesso à tabela `pedidos` do Supabase.
//
// Esse módulo substitui o modelo antigo (array JSON em app_kv.mini_pedidos
// com reescrita completa a cada operação). Aqui cada operação é por id:
// INSERT/UPDATE/DELETE numa linha. Um device com cache velho ao editar
// seu pedido NÃO consegue mais destruir pedidos de outros devices.
//
// Convenção:
// - O código do app continua trabalhando com objetos "pedido" exatamente
//   como antes (snake_case virando camelCase etc). O payload inteiro vai
//   em `payload jsonb`; colunas expostas (numero, data_venda, deletado_em,
//   vendedora, status) existem só pra índices e queries rápidas.
// - Writes são "fire-and-forget" (sem await) pra manter a UI síncrona,
//   mas por id — ao contrário do upsert cego do mini_pedidos.
// ============================================================
import { supabase } from './supabase'

function toRow(pedido) {
  return {
    id:          pedido.id,
    numero:      pedido.numero || '',
    data_venda:  pedido.dataVenda || null,
    deletado_em: pedido.deletadoEm || null,
    vendedora:   pedido.vendedora || null,
    status:      pedido.status || null,
    payload:     pedido,
  }
}

function fromRow(row) {
  // Payload tem a forma original do pedido. Colunas da tabela são só
  // "ponteiros" pros filtros. Sempre devolve o payload como fonte.
  return row?.payload ?? null
}

// ── WRITE ─────────────────────────────────────────────────────
// Upsert por id — não toca em nenhum outro pedido.
export function upsertPedidoRemoto(pedido) {
  if (!pedido?.id) return
  supabase
    .from('pedidos')
    .upsert(toRow(pedido), { onConflict: 'id' })
    .then(({ error }) => {
      if (error) {
        console.error('[pedidos] upsert error:', pedido.id, error)
        try {
          localStorage.setItem('mini_last_write_error', JSON.stringify({
            op: 'upsertPedido', id: pedido.id, msg: error?.message || String(error),
            when: new Date().toISOString(),
          }))
        } catch {}
      } else {
        try { localStorage.removeItem('mini_last_write_error') } catch {}
      }
    })
}

// Soft delete: marca deletado_em. O payload mantém deletadoEm +
// deletadoPor + motivoExclusao preservados.
export function softDeletePedidoRemoto(pedido) {
  // Essencialmente o mesmo que upsert — o chamador já setou deletadoEm
  // no pedido antes de chamar.
  upsertPedidoRemoto(pedido)
}

// Hard delete: usado por apagarPedidoDefinitivo (botão da lixeira).
export function hardDeletePedidoRemoto(id) {
  if (!id) return
  supabase
    .from('pedidos')
    .delete()
    .eq('id', id)
    .then(({ error }) => {
      if (error) console.error('[pedidos] delete error:', id, error)
    })
}

// ── READ ──────────────────────────────────────────────────────
// Puxa todos os pedidos (ativos + lixeira). O cache local em
// localStorage é atualizado por quem chamar — essa função só devolve
// o array fresco.
export async function fetchPedidosFromTabela() {
  // Supabase REST tem limite default de 1000 rows. Pedidos de uma
  // marcenaria operacional ficam na casa das centenas; 10k é teto
  // confortável. Em caso de crescer, paginar.
  const { data, error } = await supabase
    .from('pedidos')
    .select('payload')
    .order('criado_em', { ascending: false })
    .limit(10000)
  if (error) {
    console.error('[pedidos] fetch error:', error)
    return null
  }
  return (data || []).map(fromRow).filter(Boolean)
}

// Atualiza o cache local (localStorage mini_pedidos) com o que está
// na tabela. Chamada pelo useAutoSync — substitui o pull de
// app_kv.mini_pedidos que fazia parte do syncFromSupabase antigo.
//
// IMPORTANTE: isso NUNCA destrói pedidos no Supabase, só atualiza o
// cache do localStorage deste browser. É a "importação" de read.
export async function syncPedidosFromTabela() {
  const pedidos = await fetchPedidosFromTabela()
  if (!pedidos) return false
  try {
    // Usa safeSetItem (importado dinamicamente pra evitar ciclo).
    const { safeSetItem } = await import('./supabase')
    safeSetItem('mini_pedidos', JSON.stringify(pedidos))
    return true
  } catch (e) {
    console.error('[pedidos] syncPedidosFromTabela setItem failed:', e)
    return false
  }
}
